import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, ArrowRight, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { trpcClient } from "@/lib/trpc";
import { useQueryClient } from "@tanstack/react-query";

export interface QuickRelocateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tooling: {
    id: string;
    dieCutId?: string;
    plateId?: string;
    name: string;
    location?: string | null;
    storageZone?: string | null;
    storageRack?: string | null;
    storageShelf?: string | null;
    storageBin?: string | null;
  } | null;
  toolType?: "mold" | "plate";
  onSuccess?: () => void;
}

export function QuickRelocateModal({
  open,
  onOpenChange,
  tooling,
  toolType = "mold",
  onSuccess,
}: QuickRelocateModalProps) {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [storageZone, setStorageZone] = useState("");
  const [storageRack, setStorageRack] = useState("");
  const [storageShelf, setStorageShelf] = useState("");
  const [storageBin, setStorageBin] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (tooling) {
      setStorageZone(tooling.storageZone || "");
      setStorageRack(tooling.storageRack || "");
      setStorageShelf(tooling.storageShelf || "");
      setStorageBin(tooling.storageBin || "");
      setNotes("");
    }
  }, [tooling, open]);

  if (!tooling) return null;

  const code = tooling.dieCutId || tooling.plateId || tooling.id.substring(0, 8);
  const isPlate = toolType === "plate";

  const previewLocation = [
    storageZone.trim(),
    storageRack.trim() ? `Kệ ${storageRack.trim()}` : "",
    storageShelf.trim() ? `Tầng ${storageShelf.trim()}` : "",
    !isPlate && storageBin.trim() ? `Ô ${storageBin.trim()}` : "",
  ]
    .filter(Boolean)
    .join(" • ");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!storageZone.trim() && !storageRack.trim() && !storageShelf.trim() && !storageBin.trim()) {
      toast.error("Vui lòng nhập ít nhất một thông số tọa độ kệ kho!");
      return;
    }

    setIsSubmitting(true);
    try {
      if (isPlate) {
        await trpcClient.technical.plates.quickUpdate.mutate({
          id: tooling.id,
          storageZone: storageZone.trim() || undefined,
          storageRack: storageRack.trim() || undefined,
          storageShelf: storageShelf.trim() || undefined,
          location: previewLocation || undefined,
          notes: notes.trim() ? `Dời vị trí kệ kho: ${notes.trim()}` : undefined,
        });
      } else {
        await trpcClient.technical.molds.quickUpdate.mutate({
          id: tooling.id,
          storageZone: storageZone.trim() || undefined,
          storageRack: storageRack.trim() || undefined,
          storageShelf: storageShelf.trim() || undefined,
          storageBin: storageBin.trim() || undefined,
          location: previewLocation || undefined,
          notes: notes.trim() ? `Dời vị trí kệ kho: ${notes.trim()}` : undefined,
        });
      }

      toast.success(`Đã cập nhật vị trí lưu kho cho [${code}] thành công!`);
      queryClient.invalidateQueries({ queryKey: ["molds"] });
      queryClient.invalidateQueries({ queryKey: ["dieCuts"] });
      queryClient.invalidateQueries({ queryKey: ["mold", tooling.id] });
      queryClient.invalidateQueries({ queryKey: ["molds-detail"] });
      queryClient.invalidateQueries({ queryKey: ["plates"] });
      queryClient.invalidateQueries({ queryKey: ["plate", tooling.id] });
      queryClient.invalidateQueries({ queryKey: ["tooling-tracking-board"] });
      queryClient.invalidateQueries({ queryKey: ["productionOrder"] });

      onSuccess?.();
      onOpenChange(false);
    } catch (err: any) {
      toast.error("Lỗi cập nhật vị trí: " + (err.message || "Không xác định"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] p-0 overflow-hidden border-slate-200">
        <DialogHeader icon={MapPin}>
          <DialogTitle>Cập Nhật Vị Trí Kệ Kho (Đổi Vị Trí Lưu Kho)</DialogTitle>
          <p className="text-xs text-muted-foreground">
            Cập nhật tọa độ giá kệ 1-chạm cho {isPlate ? "Bản in kẽm CTP" : "Khuôn bế"}
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Tooling Summary Card */}
          <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3.5 space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                  {isPlate ? "BẢN IN KẼM" : "KHUÔN BẾ"}:
                </div>
                <div className="text-sm font-bold font-mono text-indigo-900">{code}</div>
                <div className="text-xs font-semibold text-slate-700 break-words whitespace-normal max-w-[320px]">
                  {tooling.name}
                </div>
              </div>
              <div className="text-right">
                <span className="text-[10px] text-slate-400 block">Vị trí hiện tại:</span>
                <span className="text-xs font-semibold text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 inline-block mt-0.5">
                  {tooling.location || "Chưa xếp kệ"}
                </span>
              </div>
            </div>
          </div>

          {/* Coordinates Grid */}
          <div className="space-y-3">
            <Label className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
              Tọa Độ Giá Kệ Mới:
            </Label>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold text-slate-600">
                  Khu / Dãy Kho <span className="text-rose-500">*</span>
                </Label>
                <Input
                  className="h-9 font-medium"
                  placeholder="VD: KHU_A, KHO_1"
                  value={storageZone}
                  onChange={(e) => setStorageZone(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold text-slate-600">
                  Kệ / Giá (Rack) <span className="text-rose-500">*</span>
                </Label>
                <Input
                  className="h-9 font-medium"
                  placeholder="VD: K01, R-02"
                  value={storageRack}
                  onChange={(e) => setStorageRack(e.target.value)}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold text-slate-600">Tầng (Shelf)</Label>
                <Input
                  className="h-9 font-medium"
                  placeholder="VD: T1, TANG_2"
                  value={storageShelf}
                  onChange={(e) => setStorageShelf(e.target.value)}
                />
              </div>

              {!isPlate && (
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-semibold text-slate-600">Ô / Ngăn (Bin)</Label>
                  <Input
                    className="h-9 font-medium"
                    placeholder="VD: B05, Ô-12"
                    value={storageBin}
                    onChange={(e) => setStorageBin(e.target.value)}
                  />
                </div>
              )}
            </div>

            {/* Realtime Live Preview */}
            <div className="p-2.5 rounded-lg bg-indigo-50/70 border border-indigo-200/80 flex items-center justify-between">
              <span className="text-[11px] font-semibold text-indigo-900">
                Hiển thị tổng hợp:
              </span>
              <span className="font-mono text-xs font-bold text-indigo-950">
                {previewLocation || "(Chưa có thông số)"}
              </span>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold text-slate-600">
              Lý do / Ghi chú điều chuyển (Tùy chọn)
            </Label>
            <Textarea
              className="h-16 text-xs resize-none"
              placeholder="VD: Dời sang kệ A do bổ sung khuôn mới..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <DialogFooter className="pt-2 gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isSubmitting}
              onClick={() => onOpenChange(false)}
            >
              Hủy
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={isSubmitting}
              className="bg-indigo-600 hover:bg-indigo-700 text-white min-w-[120px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  Đang lưu...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-1.5" />
                  Lưu Vị Trí Mới
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
export default QuickRelocateModal;
